import { Button, Card, InputNumber, Space } from "antd"
import VSpace from "../../common/VSpace"
import { InvoiceWorkareaGroup } from "./createInvoiceModel"
import { roundToPrecision } from "../../../helpers/math"
import { centsToDollarsDisplay } from "../../displayFormatters"
import { Action } from "./createInvoiceReducer"
import { PricingTypes } from "../../../common/constants"
import ApprovedPaymentSelect from "./ApprovedPaymentSelect"
import { CalculatedWorkareaAmount, calculateNewAmountsFromPercentageInput, calculateNewAmountsFromTotalDollarInput, getQuotedPriceTotalAmount, getQuotedPriceTotalBillableAmount, getQuotedPriceTotalUnbilledAmount } from "./quotedPriceCalculatorModel"
import { useEffect, useState } from "react"
import { hasValue } from "../../../helpers/nullable"
import DollarInput from "../../common/DollarInput"

type Props = {
  jobId: number
  workareaGroups: InvoiceWorkareaGroup[]
  dispatch: (action: Action) => void
}

const QuotedPriceCalculator = ({ jobId, workareaGroups, dispatch }: Props) => {
  const [typedAmount, setTypedAmount] = useState<number | undefined>()
  const [typedPercentage, setTypedPercentage] = useState<number | undefined>()
  
  const currentAmount = getQuotedPriceTotalAmount(workareaGroups)
  const totalUnbilled = getQuotedPriceTotalUnbilledAmount(workareaGroups)
  const totalBillable = getQuotedPriceTotalBillableAmount(workareaGroups)


  const clearTypedAmounts = () => {
    setTypedAmount(undefined)
    setTypedPercentage(undefined)
  }

  useEffect(() => {
    clearTypedAmounts()
  }, [currentAmount])

  if (!workareaGroups.some(x => x.pricingType === PricingTypes.QuotedPrice)) {
    return null
  }

  const handleApplyChange = (newValue: number | undefined) => {
    setTypedAmount(hasValue(newValue) ? Math.min(newValue, totalUnbilled) : undefined)
    setTypedPercentage(undefined)
  }

  const applyTotalChange = () => {
    if (!hasValue(typedAmount)) {
      return
    }
    updateWorkareaAmountToTotal(calculateNewAmountsFromTotalDollarInput(workareaGroups, Math.min(typedAmount, totalUnbilled)))
    clearTypedAmounts()
  }

  const updateWorkareaAmountToTotal = (newAmounts: CalculatedWorkareaAmount[]) => {
    newAmounts.forEach(x => {
      if (x.workareaId) {
        dispatch({ type: 'WORKAREA_AMOUNT_CHANGED', workareaId: x.workareaId, value: x.amount })
      } else {
        dispatch({ type: 'AMOUNT_CHANGED', groupId: x.groupId, value: x.amount })
      }
    })
  }

  const applyPercentageChange = () => {
    if (!hasValue(typedPercentage)) {
      return
    }

    const newPercentage = roundToPrecision(typedPercentage, 2) / 100
    updateWorkareaAmountToTotal(calculateNewAmountsFromPercentageInput(workareaGroups, newPercentage))
    clearTypedAmounts()
  }

  const handlePercentageChange = (value: number | null) => {
    setTypedPercentage(hasValue(value) ? Math.min(value, 100) : undefined)
    setTypedAmount(undefined)
  }

  return <Card>
    <VSpace>
      <span>How much do you want to charge for Quoted Price work areas on this invoice?</span>
      <Space size="large">
        <ApprovedPaymentSelect
          jobId={jobId}
          workareaGroups={workareaGroups}
          dispatch={action => {
            clearTypedAmounts()
            dispatch(action)
          }}
        />
        <Space>
          <DollarInput
            value={typedAmount}
            onChange={handleApplyChange}
            min={0}
            onPressEnter={applyTotalChange}
          />
          <Button
            onClick={applyTotalChange}
            disabled={!hasValue(typedAmount)}
          >Update</Button>
        </Space>
        <Space>
          <InputNumber
            value={typedPercentage}
            onChange={handlePercentageChange}
            suffix="%"
            addonAfter={<span>of {centsToDollarsDisplay(totalBillable)}</span>}
            min={0}
            onPressEnter={applyPercentageChange}
          />
          <Button
            onClick={applyPercentageChange}
            disabled={!hasValue(typedPercentage)}
          >Update</Button>
        </Space>
      </Space>
    </VSpace>
  </Card>
}

export default QuotedPriceCalculator